import { unzipSync, zipSync } from 'fflate';
import * as XLSX from 'xlsx-js-style';

/**
 * XLSX 파일을 생성하고 다운로드합니다. (틀고정 포함)
 * xlsx-js-style이 !views를 지원하지 않으므로 ZIP 후처리로 freeze pane 삽입
 * @param {Object} wb - XLSX 워크북 객체
 * @param {string} filename - 다운로드 파일명
 */
export const writeExcelFile = (wb, filename) => {
  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const files = unzipSync(new Uint8Array(buf));

  wb.SheetNames.forEach((sheetName, i) => {
    const ws = wb.Sheets[sheetName];
    const view = ws['!views']?.[0];
    if (!view || view.state !== 'frozen') return;

    const xmlKey = `xl/worksheets/sheet${i + 1}.xml`;
    if (!files[xmlKey]) return;

    const ySplit = view.ySplit || 0;
    const xSplit = view.xSplit || 0;
    if (!ySplit && !xSplit) return;

    const colLetter = (c) => {
      let col = '';
      let temp = c;
      do {
        col = String.fromCharCode(65 + (temp % 26)) + col;
        temp = Math.floor(temp / 26) - 1;
      } while (temp >= 0);
      return col;
    };

    const topLeftCell = `${xSplit > 0 ? colLetter(xSplit) : 'A'}${ySplit + 1}`;
    const activePane = ySplit > 0 && xSplit > 0 ? 'bottomRight' : ySplit > 0 ? 'bottomLeft' : 'topRight';
    const paneXml = `<pane${xSplit > 0 ? ` xSplit="${xSplit}"` : ''}${ySplit > 0 ? ` ySplit="${ySplit}"` : ''} topLeftCell="${topLeftCell}" activePane="${activePane}" state="frozen"/>`;

    let xml = new TextDecoder().decode(files[xmlKey]);
    xml = xml.replace(/(<sheetView[^>]*)\/>/, `$1>${paneXml}</sheetView>`);
    files[xmlKey] = new TextEncoder().encode(xml);
  });

  const zipped = zipSync(files, { level: 6 });
  const blob = new Blob([zipped], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * 엑셀 워크시트에 테두리와 헤더 스타일을 적용합니다.
 * @param {Object} ws - XLSX 워크시트 객체
 * @param {number} headerRowCount - 헤더 행 수 (기본값: 1)
 */
export const applyExcelStyles = (ws, headerRowCount = 1) => {
  if (!ws['!ref']) return;

  const range = parseRange(ws['!ref']);

  const border = {
    top: { style: 'thin', color: { rgb: '000000' } },
    bottom: { style: 'thin', color: { rgb: '000000' } },
    left: { style: 'thin', color: { rgb: '000000' } },
    right: { style: 'thin', color: { rgb: '000000' } },
  };

  for (let R = range.sr; R <= range.er; R++) {
    for (let C = range.sc; C <= range.ec; C++) {
      const cellRef = encodeCell(R, C);
      if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };

      if (R < headerRowCount) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: 'F2F2F2' } },
          border,
          font: { bold: true },
        };
      } else {
        ws[cellRef].s = { border };
      }
    }
  }

  ws['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: headerRowCount }];
};

/**
 * 특정 컬럼부터 가운데 정렬을 적용합니다.
 * @param {Object} ws - XLSX 워크시트 객체
 * @param {number} startCol - 가운데 정렬 시작 컬럼 인덱스 (0-based)
 */
export const applyCenterAlignment = (ws, startCol) => {
  if (!ws['!ref']) return;

  const range = parseRange(ws['!ref']);

  for (let R = range.sr; R <= range.er; R++) {
    for (let C = startCol; C <= range.ec; C++) {
      const cellRef = encodeCell(R, C);
      if (ws[cellRef]) {
        ws[cellRef].s = {
          ...ws[cellRef].s,
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    }
  }
};

/**
 * 값이 "결석"인 셀의 글자색을 빨강으로 적용합니다.
 * @param {Object} ws - XLSX 워크시트 객체
 */
export const applyAbsenceFontColor = (ws, headerRowCount = 0) => {
  if (!ws['!ref']) return;

  const range = parseRange(ws['!ref']);

  const colorMap = {
    '결석': 'FF0000',
    '출석': '008000',
  };

  for (let R = Math.max(range.sr, headerRowCount); R <= range.er; R++) {
    for (let C = range.sc; C <= range.ec; C++) {
      const cellRef = encodeCell(R, C);
      const color = ws[cellRef] && colorMap[ws[cellRef].v];
      if (color) {
        ws[cellRef].s = {
          ...ws[cellRef].s,
          font: { ...(ws[cellRef].s?.font ?? {}), color: { rgb: color } },
        };
      }
    }
  }
};

/**
 * 값이 "Pass"인 셀은 초록색, "None-Pass"인 셀은 빨간색으로 글자색을 적용합니다.
 * @param {Object} ws - XLSX 워크시트 객체
 * @param {number} headerRowCount - 건너뛸 헤더 행 수
 */
export const applyPassGradeFontColor = (ws, headerRowCount = 0) => {
  if (!ws['!ref']) return;

  const range = parseRange(ws['!ref']);

  const colorMap = {
    'Pass': '008000',
    'PASS': '008000',
    'None-Pass': 'FF0000',
    'NON_PASS': 'FF0000',
    'Non-Pass': 'FF0000',
  };

  for (let R = Math.max(range.sr, headerRowCount); R <= range.er; R++) {
    for (let C = range.sc; C <= range.ec; C++) {
      const cellRef = encodeCell(R, C);
      const color = ws[cellRef] && colorMap[ws[cellRef].v];
      if (color) {
        ws[cellRef].s = {
          ...ws[cellRef].s,
          font: { ...(ws[cellRef].s?.font ?? {}), color: { rgb: color } },
        };
      }
    }
  }
};

function parseRange(ref) {
  const parts = ref.split(':');
  const s = cellToRC(parts[0]);
  const e = parts[1] ? cellToRC(parts[1]) : s;
  return { sr: s.r, sc: s.c, er: e.r, ec: e.c };
}

function cellToRC(cell) {
  let c = 0;
  let r = 0;
  let i = 0;
  while (i < cell.length && cell.charCodeAt(i) >= 65) {
    c = c * 26 + (cell.charCodeAt(i) - 64);
    i++;
  }
  r = parseInt(cell.substring(i), 10) - 1;
  return { r, c: c - 1 };
}

function encodeCell(r, c) {
  let col = '';
  let temp = c;
  do {
    col = String.fromCharCode(65 + (temp % 26)) + col;
    temp = Math.floor(temp / 26) - 1;
  } while (temp >= 0);
  return col + (r + 1);
}
